/* =====================================================================
   mpc-glaze-archive.js
   Scroll-driven enhancements for the Glaze Archive page.

   A) Body-level background crossfade. IntersectionObserver watches every
      .glaze-entry. When an entry crosses the middle 20% of the viewport,
      we read its computed background-color and write it to body. CSS
      supplies the 600ms transition.

   B) Entry reveal. A second IntersectionObserver toggles .is-visible on
      each entry ~30% in view. CSS handles the opacity + translateY
      fade-in under prefers-reduced-motion: no-preference.

   C) GSAP ScrollTrigger motion core (v3). For each active glaze:
        - Pin .glaze-entry--active__text while the section is in view
        - Scrub the horizontal strip track with a 1s lag (scrub: 1)
        - Tween the progress-bar scaleX in lockstep
        - Stagger a gentle entry reveal on the text block
      Retired glazes get a quiet photo + text stagger reveal.

   D) Persistent top nav — pills, status toggle, jump-to sheet,
      keyboard shortcuts, current-glaze chip.

   Motion is gated off for prefers-reduced-motion: reduce and viewports
   <= 900px (mobile keeps native horizontal overflow scroll).
   ===================================================================== */

(function () {
  'use strict';

  if (window.__mpcGlazeArchiveInit) {
    return;
  }
  window.__mpcGlazeArchiveInit = true;

  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouchViewport = window.matchMedia && window.matchMedia('(max-width: 900px)').matches;

  function init() {
    var entries = document.querySelectorAll('.glaze-entry');
    if (!entries.length) return;

    if (typeof window.IntersectionObserver !== 'function') {
      entries.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    document.body.classList.add('glaze-archive-active');
    // Reserve top-padding for the fixed glaze topnav from the very top of page.
    // (CSS rule: body.glaze-topnav-active { padding-top: 64px; })
    document.body.classList.add('glaze-topnav-active');

    // ----- A) Body background crossfade -----
    var bgObserver = new IntersectionObserver(
      function (records) {
        records.forEach(function (record) {
          if (!record.isIntersecting) return;
          var el = record.target;
          var computed = window.getComputedStyle(el).backgroundColor;
          if (computed && computed !== 'rgba(0, 0, 0, 0)' && computed !== 'transparent') {
            document.body.style.backgroundColor = computed;
          } else if (el.dataset.hex) {
            document.body.style.backgroundColor = el.dataset.hex;
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );

    // ----- B) One-shot reveal (legacy fade-in for quiet entries) -----
    var revealObserver = new IntersectionObserver(
      function (records) {
        records.forEach(function (record) {
          if (record.isIntersecting) {
            record.target.classList.add('is-visible');
            revealObserver.unobserve(record.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.3 }
    );

    entries.forEach(function (el) {
      bgObserver.observe(el);
      revealObserver.observe(el);
    });

    var acts = document.querySelectorAll(
      '.glaze-prelude, .glaze-intro, .glaze-families, .glaze-closing'
    );
    acts.forEach(function (el) {
      revealObserver.observe(el);
    });

    // ----- C) GSAP motion core -----
    initGsapMotion();

    // ----- D) Persistent top nav -----
    initTopNav(entries);
  }

  function initGsapMotion() {
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;
    if (prefersReduced) return;
    // Note: mobile (<=900px) still runs entry reveals, rotation, and the
    // Acadia choreography — we only gate out the pin + horizontal scrub
    // below, which depends on a fixed-size left column that doesn't exist
    // in the mobile single-column layout.

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    // --- Active glaze entry reveal (ALL active entries, strip or not) ---
    // Runs regardless of whether a product strip is wired up, so Brad sees
    // the staggered disc/pill/name/rule/desc/hex animation on every section
    // from day one. The pin + scrub below only fires once .has-strip is set.
    var allActiveSections = document.querySelectorAll('.glaze-entry--active');
    allActiveSections.forEach(function (section) {
      var revealTargets = section.querySelectorAll(
        '.glaze-entry__swatch, .glaze-entry__family-pill, .glaze-entry__name, .glaze-entry__rule, .glaze-entry__description, .glaze-entry__hex'
      );
      if (!revealTargets.length) return;
      gsap.from(revealTargets, {
        opacity: 0,
        y: 24,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });
    });

    // --- Active glaze sections WITH a product strip: pin + horizontal scrub ---
    // Desktop only — mobile layout is single-column so there's no left
    // text column to pin and no horizontal strip to scrub.
    var activeSections = isTouchViewport
      ? []
      : document.querySelectorAll('.glaze-entry--active.has-strip');
    activeSections.forEach(function (section) {
      var textBlock = section.querySelector('.glaze-entry--active__text');
      var track = section.querySelector('.glaze-strip__track');
      var viewport = section.querySelector('.glaze-strip__viewport');
      if (!textBlock || !track || !viewport) return;

      var travel = function () {
        var t = track.scrollWidth - viewport.clientWidth + 48;
        return t > 0 ? t : 0;
      };

      // For static image demos (Acadia and friends — no horizontal product
      // strip), pin the whole grid so the figures stay on screen together
      // with the text. For product-strip sections, only the text column
      // pins while the strip translateX scrubs horizontally.
      var isStaticDemo = section.getAttribute('data-slug') === 'acadia';
      var grid = section.querySelector('.glaze-entry--active__grid');
      var pinTarget = isStaticDemo && grid ? grid : textBlock;

      ScrollTrigger.create({
        trigger: section,
        start: 'top top+=80',
        end: 'bottom bottom-=120',
        pin: pinTarget,
        pinSpacing: false,
        anticipatePin: 1,
      });

      // Horizontal scrub with 1s lag (signature GSAP feel) — skip for static
      // image demos (Acadia) where the figures are meant to stay centered.
      if (!isStaticDemo) {
        gsap.to(track, {
          x: function () { return -travel(); },
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top top+=120',
            end: 'bottom bottom-=120',
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });

        // Progress bar fill in lockstep with scroll
        var progressFill = section.querySelector('.glaze-strip__progress-fill');
        if (progressFill) {
          gsap.fromTo(
            progressFill,
            { scaleX: 0 },
            {
              scaleX: 1,
              transformOrigin: 'left center',
              ease: 'none',
              scrollTrigger: {
                trigger: section,
                start: 'top top+=120',
                end: 'bottom bottom-=120',
                scrub: true,
                invalidateOnRefresh: true,
              },
            }
          );
        }
      }
    });

    // --- Per-glaze image demos: scroll-driven 360deg rotation ---
    // Drives any [data-rotate-on-scroll] image inside an active glaze
    // section through a full rotation across the section's life in view.
    // Runs on mobile too — the rotation is the hero for Acadia.
    document.querySelectorAll('.glaze-entry--active [data-rotate-on-scroll]').forEach(function (img) {
      var section = img.closest('.glaze-entry--active');
      if (!section) return;
      gsap.to(img, {
        rotation: 360,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });
    });

    // --- Acadia: choreographed entry + exit ---
    // Vases slide in from the sides with a long eased scrub; the platter
    // tumbles in from below with scale + y + opacity. On exit all three
    // lift slightly and fade. Keeps the platter rotation running through
    // the entire section life (driven by the rotation loop above).
    document.querySelectorAll('.glaze-entry--active[data-slug="acadia"]').forEach(function (section) {
      var vases = section.querySelectorAll('.glaze-demo-card--vase');
      var platterCard = section.querySelector('.glaze-demo-card--platter');
      var leftVase = vases[0];
      var rightVase = vases[1];

      if (leftVase) {
        gsap.from(leftVase, {
          xPercent: -110,
          yPercent: 8,
          rotation: -9,
          opacity: 0,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 95%',
            end: 'top 48%',
            scrub: 1.1,
            invalidateOnRefresh: true,
          },
        });
      }
      if (rightVase) {
        gsap.from(rightVase, {
          xPercent: 110,
          yPercent: 8,
          rotation: 9,
          opacity: 0,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 95%',
            end: 'top 48%',
            scrub: 1.1,
            invalidateOnRefresh: true,
          },
        });
      }
      if (platterCard) {
        gsap.from(platterCard, {
          scale: 0.45,
          y: 120,
          opacity: 0,
          ease: 'power3.out',
          transformOrigin: '50% 50%',
          scrollTrigger: {
            trigger: section,
            start: 'top 92%',
            end: 'top 40%',
            scrub: 1.1,
            invalidateOnRefresh: true,
          },
        });
      }

      // Exit: vases slide out the OPPOSITE way they came in (so the
      // composition feels like it's flying off frame), platter drops
      // back down. All three fade out in lockstep.
      if (leftVase) {
        gsap.to(leftVase, {
          xPercent: -40,
          yPercent: -20,
          rotation: 6,
          opacity: 0,
          ease: 'power2.in',
          scrollTrigger: {
            trigger: section,
            start: 'bottom 75%',
            end: 'bottom 10%',
            scrub: 1.2,
            invalidateOnRefresh: true,
          },
        });
      }
      if (rightVase) {
        gsap.to(rightVase, {
          xPercent: 40,
          yPercent: -20,
          rotation: -6,
          opacity: 0,
          ease: 'power2.in',
          scrollTrigger: {
            trigger: section,
            start: 'bottom 75%',
            end: 'bottom 10%',
            scrub: 1.2,
            invalidateOnRefresh: true,
          },
        });
      }
      if (platterCard) {
        gsap.to(platterCard, {
          scale: 0.8,
          y: 40,
          opacity: 0,
          ease: 'power2.in',
          scrollTrigger: {
            trigger: section,
            start: 'bottom 70%',
            end: 'bottom 8%',
            scrub: 1.2,
            invalidateOnRefresh: true,
          },
        });
      }
    });

    // --- Retired glazes: photo slide-in + text stagger ---
    var retiredSections = document.querySelectorAll('.glaze-entry--retired');
    retiredSections.forEach(function (section) {
      var photo = section.querySelector('.glaze-retired__photo, .glaze-entry__archive-photo, .glaze-entry--retired__photo');
      var textChildren = section.querySelectorAll(
        '.glaze-retired__eyebrow, .glaze-entry__name, .glaze-entry__rule, .glaze-entry__description, .glaze-retired__caption, .glaze-entry--retired__caption'
      );

      if (photo) {
        gsap.from(photo, {
          opacity: 0,
          x: -40,
          duration: 1.0,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }
      if (textChildren.length) {
        gsap.from(textChildren, {
          opacity: 0,
          y: 20,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });

    // Re-measure pins after top-nav jump-to smooth scroll finishes
    window.addEventListener('mpc:glaze-jump-complete', function () {
      ScrollTrigger.refresh();
    });
  }

  function initTopNav(entries) {
    var nav = document.querySelector('[data-glaze-topnav]');
    if (!nav) return;

    var trigger = nav.querySelector('[data-glaze-jump-trigger]');
    var sheet = nav.querySelector('[data-glaze-jump-sheet]');
    var grid = nav.querySelector('[data-glaze-jump-grid]');
    var searchInput = nav.querySelector('[data-glaze-jump-search]');
    var currentChip = nav.querySelector('[data-glaze-current-chip]');
    var currentName = nav.querySelector('[data-glaze-current-name]');
    var pills = nav.querySelectorAll('.glaze-topnav__pill');
    var toggles = nav.querySelectorAll('.glaze-topnav__toggle-btn');

    if (!grid || !trigger || !sheet) return;

    // Build the glaze model from DOM
    var glazes = [];
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      var slug = e.getAttribute('data-slug') || '';
      var hex = e.getAttribute('data-hex') || '#888';
      var family = e.getAttribute('data-family') || 'specialty';
      var retired = e.getAttribute('data-retired') === 'true';
      // Extract display name from aria-label ("Name, Family glaze...")
      var aria = e.getAttribute('aria-label') || slug;
      var name = aria.split(',')[0].trim();
      glazes.push({
        slug: slug,
        hex: hex,
        family: family,
        retired: retired,
        name: name,
        el: e,
      });
    }

    if (glazes.length === 0) return;

    // Render grid cells
    var cellByIndex = [];
    glazes.forEach(function (g, idx) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'glaze-topnav__cell';
      btn.setAttribute('role', 'option');
      btn.setAttribute('data-cell-slug', g.slug);
      btn.setAttribute('data-cell-family', g.family);
      btn.setAttribute('data-cell-retired', g.retired ? 'true' : 'false');
      btn.setAttribute('data-cell-index', String(idx));
      btn.setAttribute('aria-label', g.name + (g.retired ? ', retired' : ''));
      btn.style.backgroundColor = g.hex;
      var tip = document.createElement('span');
      tip.className = 'glaze-topnav__cell-tip';
      tip.textContent = g.name;
      btn.appendChild(tip);
      btn.addEventListener('click', function () {
        jumpTo(g);
        closeSheet();
      });
      grid.appendChild(btn);
      cellByIndex.push(btn);
    });

    // ---- Filter / status state ----
    var currentFamily = 'all';
    var currentStatus = 'active'; // 'active' or 'retired'

    function applyFilter() {
      for (var i = 0; i < glazes.length; i++) {
        var g = glazes[i];
        var familyMatch = currentFamily === 'all' || g.family === currentFamily;
        var statusMatch = currentStatus === 'active' ? !g.retired : g.retired;
        var visible = familyMatch && statusMatch;
        if (visible) {
          g.el.classList.remove('is-filtered-hidden');
          cellByIndex[i].classList.remove('is-hidden');
        } else {
          g.el.classList.add('is-filtered-hidden');
          cellByIndex[i].classList.add('is-hidden');
        }
      }
      // Layout has changed — let GSAP re-measure any pins
      if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
        window.ScrollTrigger.refresh();
      }
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var fam = pill.getAttribute('data-family') || 'all';
        pills.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-selected', 'false');
        });
        pill.classList.add('is-active');
        pill.setAttribute('aria-selected', 'true');
        currentFamily = fam;
        applyFilter();
        // Scroll to first visible glaze
        for (var i = 0; i < glazes.length; i++) {
          if (!glazes[i].el.classList.contains('is-filtered-hidden')) {
            jumpTo(glazes[i]);
            break;
          }
        }
      });
    });

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var st = btn.getAttribute('data-status') || 'active';
        toggles.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');
        currentStatus = st;
        applyFilter();
        for (var i = 0; i < glazes.length; i++) {
          if (!glazes[i].el.classList.contains('is-filtered-hidden')) {
            jumpTo(glazes[i]);
            break;
          }
        }
      });
    });

    // ---- Jump-to sheet open/close ----
    function openSheet() {
      sheet.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      setTimeout(function () {
        if (searchInput) searchInput.focus();
      }, 10);
    }
    function closeSheet() {
      sheet.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (searchInput) searchInput.value = '';
      searchFilter('');
    }

    trigger.addEventListener('click', function () {
      if (sheet.hidden) openSheet();
      else closeSheet();
    });

    document.addEventListener('click', function (e) {
      if (sheet.hidden) return;
      if (!nav.contains(e.target)) closeSheet();
    });

    // ---- Search inside sheet ----
    function searchFilter(q) {
      var term = (q || '').toLowerCase().trim();
      for (var i = 0; i < glazes.length; i++) {
        var cell = cellByIndex[i];
        var matches = !term || glazes[i].name.toLowerCase().indexOf(term) !== -1;
        var filterVisible = !glazes[i].el.classList.contains('is-filtered-hidden');
        if (matches && filterVisible) {
          cell.classList.remove('is-hidden');
        } else {
          cell.classList.add('is-hidden');
        }
      }
    }
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        searchFilter(searchInput.value);
      });
    }

    // ---- Jump-to function with sticky-bar compensation + ScrollTrigger hook ----
    var jumpWatchdog = null;
    function jumpTo(g) {
      if (!g || !g.el) return;
      var rect = g.el.getBoundingClientRect();
      var y = rect.top + window.pageYOffset - 72;
      window.scrollTo({ top: y, behavior: prefersReduced ? 'auto' : 'smooth' });

      // Fire an event once the scroll settles so ScrollTrigger can
      // re-measure any pinned layouts that were affected by the jump.
      if (jumpWatchdog) clearTimeout(jumpWatchdog);
      jumpWatchdog = setTimeout(function () {
        window.dispatchEvent(new Event('mpc:glaze-jump-complete'));
      }, prefersReduced ? 50 : 720);
    }

    // ---- Current glaze indicator (sync with scroll) ----
    function setCurrent(idx) {
      if (idx < 0 || idx >= glazes.length) return;
      var g = glazes[idx];
      if (currentChip) currentChip.style.backgroundColor = g.hex;
      if (currentName) currentName.textContent = g.name;
      cellByIndex.forEach(function (c) { c.classList.remove('is-current'); });
      cellByIndex[idx].classList.add('is-current');
    }

    var currentObserver = new IntersectionObserver(
      function (records) {
        var bestRecord = null;
        records.forEach(function (r) {
          if (r.isIntersecting) {
            if (!bestRecord || r.intersectionRatio > bestRecord.intersectionRatio) {
              bestRecord = r;
            }
          }
        });
        if (bestRecord) {
          var slug = bestRecord.target.getAttribute('data-slug');
          for (var i = 0; i < glazes.length; i++) {
            if (glazes[i].slug === slug) {
              setCurrent(i);
              break;
            }
          }
        }
      },
      { rootMargin: '-30% 0px -30% 0px', threshold: [0, 0.25, 0.5, 0.75] }
    );
    glazes.forEach(function (g) {
      currentObserver.observe(g.el);
    });

    // ---- Auto-hide before closing section ----
    var closing = document.querySelector('.glaze-closing');
    if (closing) {
      var closingObserver = new IntersectionObserver(
        function (records) {
          records.forEach(function (r) {
            if (r.isIntersecting) {
              nav.classList.add('is-hiding');
            } else {
              // Only un-hide when scrolled back up past it
              if (r.boundingClientRect.top > 0) {
                nav.classList.remove('is-hiding');
              }
            }
          });
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0 }
      );
      closingObserver.observe(closing);
    }

    // ---- Keyboard shortcuts ----
    document.addEventListener('keydown', function (e) {
      // Ignore if user is typing
      var t = e.target;
      var tag = (t && t.tagName || '').toLowerCase();
      var typingInField = tag === 'input' || tag === 'textarea' || (t && t.isContentEditable);

      if (e.key === 'Escape') {
        if (!sheet.hidden) {
          closeSheet();
          trigger.focus();
          e.preventDefault();
        }
        return;
      }

      if (typingInField) return;

      if (e.key === '/' && searchInput) {
        openSheet();
        e.preventDefault();
        return;
      }

      if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') {
        moveTo(1);
        e.preventDefault();
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') {
        moveTo(-1);
        e.preventDefault();
      }
    });

    function moveTo(delta) {
      // Find current visible glaze by scroll position
      var vh = window.innerHeight;
      var midline = vh * 0.4;
      var bestIdx = 0;
      var bestDist = Infinity;
      for (var i = 0; i < glazes.length; i++) {
        if (glazes[i].el.classList.contains('is-filtered-hidden')) continue;
        var rect = glazes[i].el.getBoundingClientRect();
        var d = Math.abs(rect.top - midline);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      // Step through visible glazes only
      var next = bestIdx + delta;
      while (next >= 0 && next < glazes.length && glazes[next].el.classList.contains('is-filtered-hidden')) {
        next += delta;
      }
      if (next >= 0 && next < glazes.length) {
        jumpTo(glazes[next]);
      }
    }

    applyFilter();
  }

  function boot() {
    // A rAF after DOM ready lets fonts + layout settle before GSAP measures.
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(init);
    } else {
      init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
